// React imports
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Components
import MultiLine from '../components/MultiLine.tsx';
import Button from '../components/Button.tsx';

// Contextes
import { useInTheGame } from '../utils/InTheGameContexte.tsx';
import { useGame } from '../utils/GameContexte.tsx';

// Librairies
import gsap from 'gsap';

// Images
import Aigle from '/equipes/aigle.svg';
import Cheval from '/equipes/cheval.svg';
import Dauphin from '/equipes/dauphin.svg';
import Tigre from '/equipes/tigre.svg';
import Serpent from '/equipes/serpent.svg';
import fleche from '/equipes/fleche.svg';
import flecheGris from '/equipes/fleche-gris.svg';

// Icons
import { IoIosArrowBack } from 'react-icons/io';

function Multi() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Equipes
    const equipes: string[] = [Aigle, Cheval, Tigre, Dauphin, Serpent];
    const equipesNom: string[] = ['Aigle', 'Cheval', 'Tigre', 'Dauphin', 'Serpent'];

    // Etat des équipes
    const [iconIndex, setIconIndex] = useState<number>(0);
    const [selectedIcons, setSelectedIcons] = useState<number[]>([]);

    // Contexte du jeu
    const { toggleGame } = useInTheGame();

    // Supprimer une équipe
    const handleDeleteLine = (indexToDelete: number) => {
        setSelectedIcons((prevIcons) => prevIcons.filter((_, index) => index !== indexToDelete));
    };

    // Changer l'icone de l'équipe
    const handleArrowClick = (newIconIndex: number, lineIndex: number) => {
        setSelectedIcons((prevIcons) =>
            prevIcons.map((icon, idx) => (idx === lineIndex ? newIconIndex : icon))
        );
    };

    // Ajouter une équipe
    const handleAddButtonClick = () => {
        if (selectedIcons.length < 5) {
            const newIndex = iconIndex;
            setSelectedIcons((prevIndex) => [...prevIndex, newIndex]);
        } else {
            console.log("Vous avez déjà atteint le maximum d'équipes (5)");
        }
    };

    // Vérifier si les équipes sont uniques
    const areIconsUnique = () => {
        const set = new Set(selectedIcons);

        return set.size === selectedIcons.length;
    };

    // Liste des équipes
    const equipesList = selectedIcons.map((iconIndex, index) => (
        <MultiLine
            key={`{${index}--equipe}`}
            iconIndex={iconIndex}
            setIconIndex={setIconIndex}
            equipes={equipes}
            equipesNom={equipesNom}
            onDelete={() => handleDeleteLine(index)}
            onArrowClick={(newIconIndex) => handleArrowClick(newIconIndex, index)}
        />
    ));

    // Variables du jeu issues du contexte
    const {
        setTotalPlayers,
        setEquipesNoms,
        setEquipesIcons,
        setActivePlayer,
        resetEquipesPoints,
    } = useGame();

    // Animation de la page
    useEffect(() => {
        // Activer le jeu pour affichage du logo
        toggleGame(true);

        // Réinitialiser les points et le numéro de la question au montage
        setActivePlayer(0);
        resetEquipesPoints();

        const elements = document.querySelectorAll('.multi__content');

        if (elements) {
            gsap.set(elements, { autoAlpha: 0 }); // From state
            gsap.to(elements, {
                // To state
                autoAlpha: 1,
                duration: 1, // Duration in seconds
                ease: 'power1.out', // Easing function
                stagger: 0.2, // Delay between the start of each element's animation
            });
        }
    }, [location.pathname]);

    // Démarrer le jeu
    const startGame = () => {
        setTotalPlayers(selectedIcons.length);
        const newEquipesNoms = selectedIcons.map((index) => equipesNom[index]);
        const newEquipesIcons = selectedIcons.map((index) => equipes[index]);
        const playersEquipesNoms = [];
        const playersEquipesIcons = [];
        for (let i = 0; i < selectedIcons.length; i++) {
            playersEquipesNoms.push(newEquipesIcons[i]);
            playersEquipesIcons.push(newEquipesNoms[i]);
        }
        setEquipesIcons(playersEquipesNoms);
        setEquipesNoms(playersEquipesIcons);

        navigate(`/${id}/jeu`);
    };

    // Retour à la page précédente
    const backToOldPage = () => {
        navigate(`/${id}/`);
    };

    return (
        <div className='multi'>
            <div className='multi__content'>
                <p className='text-page-semib'>Constituez les équipes :</p>
                {equipesList}

                <ul className='multi__list'>
                    {selectedIcons.length < 5 && (
                        <>
                            <li>
                                <img
                                    src={flecheGris}
                                    alt={fleche}
                                />
                            </li>
                            <li className='multi__icon inactive'></li>
                            <li className='multi__fleche'>
                                <img
                                    src={flecheGris}
                                    alt={fleche}
                                />
                            </li>
                        </>
                    )}
                    <li>
                        <Button
                            scene='add'
                            colorName='white'
                            isSelected={false}
                            onClick={handleAddButtonClick}
                            disabled={selectedIcons.length >= 5}
                        >
                            Ajouter
                        </Button>
                    </li>
                </ul>
            </div>
            <div className='multi__list'>
                <Button
                    scene='back'
                    colorName='blue'
                    isSelected={false}
                    onClick={() => backToOldPage()}
                >
                    <IoIosArrowBack />
                </Button>

                <Button
                    scene='menu'
                    colorName='red'
                    isSelected={false}
                    onClick={() => startGame()}
                    disabled={!areIconsUnique() || selectedIcons.length < 2}
                >
                    Commencer
                </Button>
            </div>
        </div>
    );
}

export default Multi;
